import React, {
  FC,
  memo,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { Loader } from '@app/components';
import {
  checkPhoneNumber,
  signIn,
  sendTelegramCode,
  signInByService,
  sendPasswordResetCode,
  verifyPasswordResetCode,
  resetPassword,
} from '@app/api';
import Logo from '../SignIn/logo.png';
import { useForm } from 'forms/hooks';
import { Button } from 'components/ui/button';
import { Eye, EyeOff, Send, Info } from 'lucide-react';
import { AuthType } from '@app/models';

declare global {
  interface Window {
    grecaptcha: {
      render: (container: string | HTMLElement, parameters: any) => number;
      getResponse: (widgetId?: number) => string;
      reset: (widgetId?: number) => void;
      ready: (callback: () => void) => void;
    };
  }
}

interface SignInNewFormProps {
  onSuccess: (accessToken: string) => void;
  onClickRegister: () => void;
  onClick?: () => void;
}

export interface SignInNewFormData {
  phone: string;
  password: string;
  confirmPassword: string;
  code: string;
  captchaToken: string | null;
}

const SignInNewForm: FC<SignInNewFormProps> = ({
  onSuccess,
  onClickRegister,
  onClick,
}) => {
  const [step, setStep] = useState<number>(0); // 0: phone, 1: password/code, 2: password reset code, 3: new password
  const [pending, setPending] = useState(false);
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [showConfirmPassword, setShowConfirmPassword] =
    useState<boolean>(false);
  const [displayPhone, setDisplayPhone] = useState('');
  const [authType, setAuthType] = useState<AuthType | null>(null);
  const [isTelegramVerified, setIsTelegramVerified] = useState(false);
  const [hasPassword, setHasPassword] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);
  const [showTelegramInfo, setShowTelegramInfo] = useState(false);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);

  const phoneRef = useRef<HTMLInputElement | null>(null);
  const passwordRef = useRef<HTMLInputElement | null>(null);
  const confirmPasswordRef = useRef<HTMLInputElement | null>(null);
  const codeRef = useRef<HTMLInputElement | null>(null);
  const captchaRef = useRef<HTMLDivElement>(null);
  const captchaWidgetId = useRef<number | null>(null);
  const captchaTokenRef = useRef<string | null>(null);

  const { values, onChange, errors, setErrors } = useForm<SignInNewFormData>({
    phone: '',
    password: '',
    confirmPassword: '',
    code: '',
    captchaToken: null,
  });

  useEffect(() => {
    if (step === 0) {
      phoneRef.current?.focus();
    } else if (step === 1 && authType === AuthType.PASSWORD) {
      passwordRef.current?.focus();
    } else if (step === 1 && authType === AuthType.TELEGRAM) {
      codeRef.current?.focus();
    } else if (step === 2) {
      codeRef.current?.focus();
    } else if (step === 3) {
      passwordRef.current?.focus();
    }
  }, [step, authType]);

  useEffect(() => {
    if (resendTimer > 0) {
      const timer = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendTimer]);

  useEffect(() => {
    const value = values.phone || '';
    let formatted = '';
    if (value.length > 0) formatted = '(' + value.substring(0, 3);
    if (value.length >= 4) formatted += ') ' + value.substring(3, 6);
    if (value.length >= 7) formatted += '-' + value.substring(6, 8);
    if (value.length >= 9) formatted += '-' + value.substring(8, 10);
    setDisplayPhone(formatted);
  }, [values.phone]);

  const handleCaptchaChange = useCallback((token: string | null) => {
    setCaptchaToken(token);
    captchaTokenRef.current = token;
  }, []);

  const initializeCaptcha = useCallback(() => {
    if (window.grecaptcha && captchaRef.current && !captchaWidgetId.current) {
      if (captchaWidgetId.current !== null) {
        window.grecaptcha.reset(captchaWidgetId.current);
      }

      const widgetId = window.grecaptcha.render(captchaRef.current, {
        sitekey: '6Les8tsqAAAAAE1146YpRWz5mcMTWqNj1U-wZzpN',
        callback: handleCaptchaChange,
      });
      captchaWidgetId.current = widgetId;
    }
  }, [handleCaptchaChange]);

  useEffect(() => {
    if ((step === 1 && authType === AuthType.PASSWORD) || step === 3) {
      if (window.grecaptcha) {
        window.grecaptcha.ready(initializeCaptcha);
      } else {
        const script = document.createElement('script');
        script.src = 'https://www.google.com/recaptcha/api.js';
        script.async = true;
        script.defer = true;
        script.onload = () => {
          window.grecaptcha.ready(initializeCaptcha);
        };
        document.head.appendChild(script);
      }
    }
  }, [step, authType, initializeCaptcha]);

  useEffect(() => {
    return () => {
      if (captchaWidgetId.current !== null && window.grecaptcha) {
        window.grecaptcha.reset(captchaWidgetId.current);
      }
    };
  }, []);

  const handlePhoneSubmit = async () => {
    if (values.phone.length !== 10) {
      setErrors({ phone: 'Введите корректный номер телефона' });
      return;
    }

    setPending(true);
    try {
      const response = await checkPhoneNumber({ phone: values.phone });

      if (!response.exists) {
        setErrors({ phone: 'Пользователь с таким номером не зарегистрирован' });
        setPending(false);
        return;
      }

      // User exists, check if has password
      setHasPassword(response.hasPassword || false);
      setIsTelegramVerified(response.isTelegramVerified || false);

      if (response.hasPassword) {
        // User has password, proceed to password step
        setAuthType(AuthType.PASSWORD);
        setStep(1);
      } else {
        // User doesn't have password, show message to set it
        setErrors({
          phone: 'У вас нет пароля. Нажмите "Забыли пароль?" чтобы установить',
        });
        setAuthType(AuthType.PASSWORD);
        setStep(1);
      }
      setErrors({});
    } catch (e: any) {
      setErrors({ phone: e.response?.data?.message || 'Произошла ошибка' });
    } finally {
      setPending(false);
    }
  };

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const token = captchaTokenRef.current;
    if (!token) {
      setErrors({ password: 'Подтвердите, что вы не робот' });
      return;
    }

    setPending(true);
    try {
      const response = await signIn({
        phone: values.phone,
        password: values.password,
        captchaToken: token,
      });
      onSuccess(response.accessToken);
    } catch (e: any) {
      setErrors({ password: e.response?.data?.message || 'Неверный пароль' });
    } finally {
      setPending(false);
    }
  };

  const handleCodeSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (values.code.length !== 6) {
      setErrors({ code: 'Введите 6-значный код' });
      return;
    }

    setPending(true);
    try {
      const response = await signInByService({
        phone: values.phone,
        code: values.code,
      });
      onSuccess(response.accessToken);
    } catch (e: any) {
      setErrors({ code: e.response?.data?.message || 'Неверный код' });
    } finally {
      setPending(false);
    }
  };

  const handleTelegramLogin = async () => {
    if (values.phone.length !== 10) {
      setErrors({ phone: 'Введите корректный номер телефона' });
      return;
    }

    setPending(true);
    try {
      const response = await checkPhoneNumber({ phone: values.phone });

      if (!response.exists) {
        // User doesn't exist, show error message
        setErrors({
          phone: 'Пользователь не найден. Пожалуйста, зарегистрируйтесь',
        });
        setPending(false);
        return;
      }

      // User exists
      if (!response.isTelegramVerified) {
        // Not verified yet, redirect to Telegram for verification
        const botName =
          process.env.REACT_APP_TELEGRAM_BOT_NAME || 'smrhub_app_test_bot';
        const botUrl = `https://t.me/${botName}?start=7${values.phone}-web`;
        window.location.href = botUrl;
        setPending(false);
      } else {
        // Already verified, send code via Telegram
        await sendTelegramCode({ phone: values.phone });
        setAuthType(AuthType.TELEGRAM);
        setIsTelegramVerified(true);
        setStep(1);
        setErrors({});
        setPending(false);
      }
    } catch (e: any) {
      setErrors({ phone: e.response?.data?.message || 'Произошла ошибка' });
      setPending(false);
    }
  };

  const handleForgotPassword = async () => {
    setPending(true);
    try {
      await sendPasswordResetCode({ phone: values.phone });
      setStep(2); // Move to code input step
      setResendTimer(30);
      setErrors({});
    } catch (e: any) {
      setErrors({
        password: e.response?.data?.message || 'Ошибка отправки кода',
      });
    } finally {
      setPending(false);
    }
  };

  const handlePasswordResetCodeSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (values.code.length !== 6) {
      setErrors({ code: 'Введите 6-значный код' });
      return;
    }

    setPending(true);
    try {
      await verifyPasswordResetCode({
        phone: values.phone,
        code: values.code,
      });
      setStep(3); // Move to new password step
      setErrors({});
    } catch (e: any) {
      setErrors({ code: e.response?.data?.message || 'Неверный код' });
    } finally {
      setPending(false);
    }
  };

  const handleNewPasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (values.password.length < 8) {
      setErrors({ password: 'Пароль должен содержать 8 или более символов' });
      return;
    }

    if (!/[a-z]/.test(values.password)) {
      setErrors({
        password: 'Пароль должен содержать как минимум 1 строчную букву',
      });
      return;
    }

    if (!/[A-Z]/.test(values.password)) {
      setErrors({
        password: 'Пароль должен содержать как минимум 1 заглавную букву',
      });
      return;
    }

    if (!/\d/.test(values.password)) {
      setErrors({
        password: 'Пароль должен содержать как минимум 1 цифру',
      });
      return;
    }

    if (!/[!@#$%^&*(),.?":{}|<>]/.test(values.password)) {
      setErrors({
        password: 'Пароль должен содержать как минимум 1 специальный символ',
      });
      return;
    }

    if (values.password !== values.confirmPassword) {
      setErrors({ confirmPassword: 'Пароли не совпадают' });
      return;
    }

    setPending(true);
    try {
      // First reset the password
      await resetPassword({
        phone: values.phone,
        code: values.code,
        password: values.password,
      });

      // Then sign in with the new password
      const token = captchaTokenRef.current;
      if (!token) {
        setErrors({ password: 'Подтвердите, что вы не робот' });
        setPending(false);
        return;
      }

      const signInResponse = await signIn({
        phone: values.phone,
        password: values.password,
        captchaToken: token,
      });
      onSuccess(signInResponse.accessToken);
    } catch (e: any) {
      const errorMessage =
        e.response?.data?.message || 'Ошибка установки пароля';
      setErrors({ password: errorMessage });
    } finally {
      setPending(false);
    }
  };

  const handleResendCode = async () => {
    if (resendTimer > 0) return;

    setPending(true);
    try {
      await sendPasswordResetCode({ phone: values.phone });
      setResendTimer(30);
      setErrors({});
    } catch (e: any) {
      setErrors({ code: e.response?.data?.message || 'Ошибка отправки кода' });
    } finally {
      setPending(false);
    }
  };

  return (
    <div className="w-full h-full overflow-y-auto bg-white">
      <div className="min-h-full p-6 flex justify-center items-start">
        <div className="w-full max-w-[296px]">
          <div className="mb-6">
            <div className="flex items-center gap-[5px]">
              <img src={Logo} className="w-[30.59px] h-[40px]" alt="logo" />
              <div className="flex flex-col justify-center">
                <h2 className="font-medium text-lg sm:text-[21px] leading-[130%] m-0 p-0 text-black">
                  SMR
                </h2>
                <p className="font-normal text-xs tracking-[0.25px] text-[#757575] m-0 p-0 leading-tight">
                  Поиск подряда и поставщиков
                </p>
              </div>
            </div>
          </div>

          <div className="w-full">
            {step !== 0 && (
              <button
                type="button"
                onClick={() => {
                  if (step === 1) {
                    setStep(0);
                    setAuthType(null);
                    setErrors({});
                  } else if (step === 2 || step === 3) {
                    setStep(1);
                    setErrors({});
                  }
                }}
                className="mb-4 text-sm font-medium flex items-center gap-1"
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: '#3A57E8',
                  cursor: 'pointer',
                }}
              >
                <span>←</span> Назад
              </button>
            )}
            <h1 className="font-medium text-lg sm:text-2xl text-[#424242] mb-4 sm:mb-6 leading-tight">
              {step === 0
                ? 'Вход'
                : step === 2
                ? 'Восстановление пароля'
                : step === 3
                ? hasPassword
                  ? 'Новый пароль'
                  : 'Установка пароля'
                : 'Введите пароль'}
            </h1>

            <form
              onSubmit={
                step === 1 && authType === AuthType.PASSWORD
                  ? handlePasswordSubmit
                  : step === 1 && authType === AuthType.TELEGRAM
                  ? handleCodeSubmit
                  : (e) => e.preventDefault()
              }
              className="flex flex-col gap-3"
            >
              {step === 0 && (
                <div className="flex flex-col gap-2">
                  <div
                    className="w-full h-[52px] rounded-lg px-4 py-1 flex flex-col justify-center"
                    style={{
                      background: '#F5F5F5',
                      border: '1px solid #C3DFF7',
                    }}
                  >
                    <label
                      htmlFor="phone"
                      className="text-xs mb-0"
                      style={{ color: '#757575' }}
                    >
                      Номер телефона
                    </label>
                    <input
                      id="phone"
                      type="text"
                      disabled={pending}
                      onChange={(e) => {
                        let value = e.target.value.replace(/\D/g, '');
                        value = value.slice(0, 10);
                        onChange(value, 'phone');
                      }}
                      value={displayPhone}
                      name="phone"
                      placeholder="+7 (___) __ - __ - __"
                      ref={phoneRef}
                      className="w-full text-base font-bold border-0 outline-none p-0 m-0"
                      style={{
                        background: 'transparent',
                        color: '#0A0A0A',
                        lineHeight: '20px',
                      }}
                    />
                  </div>
                  {errors.phone && (
                    <span className="text-red-500 text-xs">{errors.phone}</span>
                  )}

                  <Button
                    type="button"
                    onClick={handlePhoneSubmit}
                    className="w-full h-[52px] rounded-lg mt-3"
                    disabled={values.phone.length !== 10 || pending}
                    style={{
                      background:
                        values.phone.length === 10 ? '#09345F' : '#C3DFF7',
                      color: '#FFFFFF',
                    }}
                  >
                    {pending ? (
                      <Loader size="small" color="primary" />
                    ) : (
                      <span className="font-medium text-base">Продолжить</span>
                    )}
                  </Button>

                  <div className="flex items-center my-2">
                    <div
                      className="flex-1 h-px"
                      style={{ background: '#E0E0E0' }}
                    ></div>
                    <span className="px-3 text-sm" style={{ color: '#757575' }}>
                      или
                    </span>
                    <div
                      className="flex-1 h-px"
                      style={{ background: '#E0E0E0' }}
                    ></div>
                  </div>

                  <div className="flex items-center gap-2">
                    <Button
                      type="button"
                      onClick={handleTelegramLogin}
                      className="flex-1 h-[52px] rounded-lg flex items-center justify-center gap-2"
                      style={{
                        background: '#00BCD4',
                        color: '#FFFFFF',
                      }}
                      disabled={pending}
                    >
                      <Send size={20} />
                      <span className="font-medium text-base">
                        Войти через Telegram
                      </span>
                    </Button>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowTelegramInfo(!showTelegramInfo);
                      }}
                      className="h-[52px] w-[52px] rounded-lg flex items-center justify-center"
                      style={{
                        background: '#F5F5F5',
                        border: '1px solid #E0E0E0',
                        cursor: 'pointer',
                      }}
                    >
                      <Info size={20} style={{ color: '#757575' }} />
                    </button>
                  </div>

                  {showTelegramInfo && (
                    <div
                      className="text-xs px-3 py-2 rounded-lg"
                      style={{
                        color: '#424242',
                        backgroundColor: '#F5F5F5',
                        border: '1px solid #E0E0E0',
                        lineHeight: '16px',
                      }}
                    >
                      При первом входе нужно подтвердить номер в Telegram. После
                      подтверждения код будет приходить автоматически.
                    </div>
                  )}

                  <button
                    type="button"
                    onClick={onClickRegister}
                    className="mt-4 text-sm text-center underline"
                    style={{
                      background: 'transparent',
                      border: 'none',
                      color: '#3A57E8',
                      cursor: 'pointer',
                    }}
                  >
                    Зарегистрироваться
                  </button>
                </div>
              )}{' '}
              {step === 1 && authType === AuthType.PASSWORD && (
                <div className="flex flex-col gap-3">
                  <div
                    className="w-full h-[52px] rounded-lg px-4 py-1 flex items-center justify-between"
                    style={{
                      background: '#F5F5F5',
                      border: '1px solid #C3DFF7',
                    }}
                  >
                    <div className="flex flex-col flex-1">
                      <label
                        htmlFor="password"
                        className="text-xs"
                        style={{ color: '#757575' }}
                      >
                        Пароль
                      </label>
                      <input
                        id="password"
                        type={showPassword ? 'text' : 'password'}
                        disabled={pending}
                        onChange={(e) => onChange(e.target.value, 'password')}
                        value={values.password}
                        name="password"
                        placeholder="••••••••"
                        ref={passwordRef}
                        className="w-full text-base font-bold border-0 outline-none p-0 m-0"
                        style={{
                          background: 'transparent',
                          color: '#0A0A0A',
                          lineHeight: '20px',
                        }}
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="ml-2"
                      style={{
                        background: 'transparent',
                        border: 'none',
                        cursor: 'pointer',
                      }}
                    >
                      {showPassword ? (
                        <EyeOff size={20} style={{ color: '#757575' }} />
                      ) : (
                        <Eye size={20} style={{ color: '#757575' }} />
                      )}
                    </button>
                  </div>
                  {errors.password && (
                    <span className="text-red-500 text-xs">
                      {errors.password}
                    </span>
                  )}

                  <div className="mt-2">
                    <div
                      ref={captchaRef}
                      className="w-full max-w-full overflow-hidden"
                    ></div>
                  </div>

                  <Button
                    type="submit"
                    className="w-full h-[52px] rounded-lg"
                    disabled={!hasPassword || pending}
                    style={{
                      background: hasPassword ? '#09345F' : '#C3DFF7',
                      color: '#FFFFFF',
                    }}
                  >
                    {pending ? (
                      <Loader size="small" color="primary" />
                    ) : (
                      <span className="font-medium text-base">Войти</span>
                    )}
                  </Button>

                  {!hasPassword && (
                    <div
                      className="text-sm text-center"
                      style={{ color: '#a83e3eff' }}
                    >
                      У вас нет пароля. Нажмите кнопку ниже, чтобы установить
                    </div>
                  )}

                  <button
                    type="button"
                    onClick={handleForgotPassword}
                    className="text-sm text-center underline"
                    style={{
                      background: 'transparent',
                      border: 'none',
                      color: '#3A57E8',
                      cursor: 'pointer',
                    }}
                    disabled={pending}
                  >
                    {hasPassword ? 'Забыли пароль?' : 'Установить пароль'}
                  </button>
                </div>
              )}
              {step === 1 && authType === AuthType.TELEGRAM && (
                <div className="flex flex-col gap-3">
                  <div className="mb-2 text-sm" style={{ color: '#757575' }}>
                    Введите код из Telegram
                  </div>

                  <div
                    className="w-full h-[52px] rounded-lg px-4 py-1 flex flex-col justify-center"
                    style={{
                      background: '#F5F5F5',
                      border: '1px solid #C3DFF7',
                    }}
                  >
                    <label
                      htmlFor="code"
                      className="text-xs mb-0"
                      style={{ color: '#757575' }}
                    >
                      Код подтверждения
                    </label>
                    <input
                      id="code"
                      type="text"
                      disabled={pending}
                      onChange={(e) => {
                        const value = e.target.value
                          .replace(/\D/g, '')
                          .slice(0, 6);
                        onChange(value, 'code');
                      }}
                      value={values.code}
                      name="code"
                      placeholder="123321"
                      ref={codeRef}
                      maxLength={6}
                      className="w-full text-base font-bold border-0 outline-none p-0 m-0"
                      style={{
                        background: 'transparent',
                        color: '#0A0A0A',
                        lineHeight: '20px',
                      }}
                    />
                  </div>
                  {errors.code && (
                    <span className="text-red-500 text-xs">{errors.code}</span>
                  )}

                  <Button
                    type="submit"
                    className="w-full h-[52px] rounded-lg"
                    disabled={values.code.length !== 6 || pending}
                    style={{
                      background:
                        values.code.length === 6 ? '#09345F' : '#C3DFF7',
                      color: '#FFFFFF',
                    }}
                  >
                    {pending ? (
                      <Loader size="small" color="primary" />
                    ) : (
                      <span className="font-medium text-base">Войти</span>
                    )}
                  </Button>
                </div>
              )}
              {step === 2 && (
                <div className="flex flex-col gap-3">
                  <div className="mb-2 text-sm" style={{ color: '#757575' }}>
                    Введите код из SMS
                  </div>

                  <div
                    className="w-full h-[52px] rounded-lg px-4 py-1 flex flex-col justify-center"
                    style={{
                      background: '#F5F5F5',
                      border: '1px solid #C3DFF7',
                    }}
                  >
                    <label
                      htmlFor="code"
                      className="text-xs mb-0"
                      style={{ color: '#757575' }}
                    >
                      Код подтверждения
                    </label>
                    <input
                      id="code"
                      type="text"
                      disabled={pending}
                      onChange={(e) => {
                        const value = e.target.value
                          .replace(/\D/g, '')
                          .slice(0, 6);
                        onChange(value, 'code');
                      }}
                      value={values.code}
                      name="code"
                      placeholder="123321"
                      ref={codeRef}
                      maxLength={6}
                      className="w-full text-base font-bold border-0 outline-none p-0 m-0"
                      style={{
                        background: 'transparent',
                        color: '#0A0A0A',
                        lineHeight: '20px',
                      }}
                    />
                  </div>
                  {errors.code && (
                    <span className="text-red-500 text-xs">{errors.code}</span>
                  )}

                  <Button
                    type="button"
                    onClick={handlePasswordResetCodeSubmit}
                    className="w-full h-[52px] rounded-lg"
                    disabled={values.code.length !== 6 || pending}
                    style={{
                      background:
                        values.code.length === 6 ? '#09345F' : '#C3DFF7',
                      color: '#FFFFFF',
                    }}
                  >
                    {pending ? (
                      <Loader size="small" color="primary" />
                    ) : (
                      <span className="font-medium text-base">Продолжить</span>
                    )}
                  </Button>

                  <button
                    type="button"
                    onClick={handleResendCode}
                    className="text-sm text-center"
                    disabled={resendTimer > 0 || pending}
                    style={{
                      background: 'transparent',
                      border: 'none',
                      color: resendTimer > 0 ? '#C3DFF7' : '#3A57E8',
                      cursor: resendTimer > 0 ? 'not-allowed' : 'pointer',
                    }}
                  >
                    {resendTimer > 0
                      ? `Отправить код повторно (${resendTimer})`
                      : 'Отправить код повторно'}
                  </button>
                </div>
              )}
              {step === 3 && (
                <div className="flex flex-col gap-3">
                  <div className="mb-2 text-sm" style={{ color: '#757575' }}>
                    {hasPassword ? 'Введите новый пароль' : 'Установите пароль'}
                  </div>
                  <p className="text-xs mb-3" style={{ color: '#757575' }}>
                    Пароль должен содержать минимум 8 символов, заглавные и
                    строчные буквы, цифры
                  </p>

                  <div
                    className="w-full h-[52px] rounded-lg px-4 py-1 flex items-center justify-between"
                    style={{
                      background: '#F5F5F5',
                      border: '1px solid #C3DFF7',
                    }}
                  >
                    <div className="flex flex-col flex-1">
                      <label
                        htmlFor="password"
                        className="text-xs"
                        style={{ color: '#757575' }}
                      >
                        Пароль
                      </label>
                      <input
                        id="password"
                        type={showPassword ? 'text' : 'password'}
                        disabled={pending}
                        onChange={(e) => onChange(e.target.value, 'password')}
                        value={values.password}
                        name="password"
                        placeholder="••••••••"
                        ref={passwordRef}
                        className="w-full text-base font-bold border-0 outline-none p-0 m-0"
                        style={{
                          background: 'transparent',
                          color: '#0A0A0A',
                          lineHeight: '20px',
                        }}
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="ml-2"
                      style={{
                        background: 'transparent',
                        border: 'none',
                        cursor: 'pointer',
                      }}
                    >
                      {showPassword ? (
                        <EyeOff size={20} style={{ color: '#757575' }} />
                      ) : (
                        <Eye size={20} style={{ color: '#757575' }} />
                      )}
                    </button>
                  </div>
                  {errors.password && (
                    <span className="text-red-500 text-xs mt-1">
                      {errors.password}
                    </span>
                  )}

                  <div
                    className="w-full h-[52px] rounded-lg px-4 py-1 flex items-center justify-between mt-3"
                    style={{
                      background: '#F5F5F5',
                      border: '1px solid #C3DFF7',
                    }}
                  >
                    <div className="flex flex-col flex-1">
                      <label
                        htmlFor="confirmPassword"
                        className="text-xs"
                        style={{ color: '#757575' }}
                      >
                        Подтвердите пароль
                      </label>
                      <input
                        id="confirmPassword"
                        type={showConfirmPassword ? 'text' : 'password'}
                        disabled={pending}
                        onChange={(e) =>
                          onChange(e.target.value, 'confirmPassword')
                        }
                        value={values.confirmPassword}
                        name="confirmPassword"
                        placeholder="••••••••"
                        ref={confirmPasswordRef}
                        className="w-full text-base font-bold border-0 outline-none p-0 m-0"
                        style={{
                          background: 'transparent',
                          color: '#0A0A0A',
                          lineHeight: '20px',
                        }}
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() =>
                        setShowConfirmPassword(!showConfirmPassword)
                      }
                      className="ml-2"
                      style={{
                        background: 'transparent',
                        border: 'none',
                        cursor: 'pointer',
                      }}
                    >
                      {showConfirmPassword ? (
                        <EyeOff size={20} style={{ color: '#757575' }} />
                      ) : (
                        <Eye size={20} style={{ color: '#757575' }} />
                      )}
                    </button>
                  </div>
                  {errors.confirmPassword && (
                    <span className="text-red-500 text-xs mt-1">
                      {errors.confirmPassword}
                    </span>
                  )}

                  <div className="mt-2">
                    <div
                      ref={captchaRef}
                      className="w-full max-w-full overflow-hidden"
                    ></div>
                  </div>

                  <Button
                    type="button"
                    onClick={handleNewPasswordSubmit}
                    className="w-full h-[52px] rounded-lg"
                    disabled={
                      !values.password || !values.confirmPassword || pending
                    }
                    style={{
                      background:
                        values.password && values.confirmPassword
                          ? '#09345F'
                          : '#C3DFF7',
                      color: '#FFFFFF',
                    }}
                  >
                    {pending ? (
                      <Loader size="small" color="primary" />
                    ) : (
                      <span className="font-medium text-base">
                        {hasPassword ? 'Изменить пароль' : 'Установить пароль'}
                      </span>
                    )}
                  </Button>
                </div>
              )}
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default memo(SignInNewForm);
